/** Photo size rules shared with the generator: under the floor is refused, under full is enlarged. */
export const MIN_PHOTO_EDGE = 640;
export const FULL_PHOTO_EDGE = 1080;
export const SMALL_PHOTO_NOTE = 'Small photo: it will be enlarged for posts';

export type PhotoCheck = { ok: true; small: boolean } | { ok: false; reason: string };

/**
 * Long edge in pixels, or null when the browser cannot decode the file (videos, HEIC, or a
 * runtime without createImageBitmap such as jsdom). Unknown sizes are left to the generator.
 */
export async function imageLongEdge(file: File): Promise<number | null> {
  if (!file.type.startsWith('image/') || typeof createImageBitmap !== 'function') return null;
  try {
    const bitmap = await createImageBitmap(file);
    const edge = Math.max(bitmap.width, bitmap.height);
    bitmap.close();
    return edge;
  } catch {
    return null;
  }
}

/** Decides before upload whether a photo can be used, and whether it will be enlarged. */
export async function checkPhoto(file: File): Promise<PhotoCheck> {
  const edge = await imageLongEdge(file);
  if (edge === null) return { ok: true, small: false };
  if (edge < MIN_PHOTO_EDGE)
    return {
      ok: false,
      reason: `Photos need to be at least ${MIN_PHOTO_EDGE} px on the long side`,
    };
  return { ok: true, small: edge < FULL_PHOTO_EDGE };
}
